import { Tween, Easing, type EasingFn, type TweenOptions } from './Tween';

/**
 * TweenHost — singleton driver for the substrate-agnostic Tween primitive.
 *
 * Phase H10 Arc β (2026-05-08): replaces `scene.tweens.add(...)` callers
 * across the gameplay layer. The Tween primitive (Tween.ts) has existed
 * since H-B Stage 4b but was used directly in only a few places that
 * supplied their own tick driver (PixiOverlayHost, CameraManager). This
 * host centralizes the driver so every consumer can fire-and-forget.
 *
 * Usage:
 *   import { TweenHost } from '../rendering/TweenHost';
 *   const handle = TweenHost.add(target, { to: { x: 100 }, durationMs: 500 });
 *   // ...later, to cancel mid-flight:
 *   handle.kill();
 *
 * Driver: see `src/game/rendering/TweenHostDriver.ts` for the rAF wiring.
 * Tests can call `step(dt)` manually to advance time without a real loop.
 */

export interface TweenHostHandle {
  /** Cancel mid-flight. onComplete will NOT fire. */
  kill(): void;
  /** True until the tween settles or kill() is called. */
  isAlive(): boolean;
}

class TweenHostImpl {
  private active: Tween[] = [];

  /**
   * Register a new tween. Returns a handle for cancellation. The
   * underlying Tween reads start values from `target` at construction
   * (matches Tween.ts semantics) so subsequent target-property writes
   * by other code are not picked up — the tween animates from the
   * snapshot at add() time.
   */
  add(target: object, opts: TweenOptions): TweenHostHandle {
    const t = new Tween(target, opts);
    this.active.push(t);
    return {
      kill: () => t.kill(),
      isAlive: () => t.isAlive(),
    };
  }

  /**
   * Register a tween that waits `delayMs` before reading the start
   * values from the target and beginning to animate. Mirrors Phaser's
   * tween `delay` option. Returns a handle whose kill() cancels both
   * the pending start AND the in-flight animation if it has begun.
   */
  addDelayed(target: object, delayMs: number, opts: TweenOptions): TweenHostHandle {
    if (delayMs <= 0) return this.add(target, opts);
    let inner: TweenHostHandle | null = null;
    let killed = false;
    const timer = setTimeout(() => {
      if (killed) return;
      inner = this.add(target, opts);
    }, delayMs);
    return {
      kill: () => {
        killed = true;
        clearTimeout(timer);
        inner?.kill();
      },
      isAlive: () => {
        if (killed) return false;
        return inner ? inner.isAlive() : true; // pre-start counts as alive
      },
    };
  }

  /**
   * Advance every active tween by `deltaMs`. Tweens that complete or
   * have been killed are removed from the active list. Called by the
   * driver each frame.
   */
  step(deltaMs: number): void {
    if (this.active.length === 0) return;
    // Iterate over a snapshot so onComplete callbacks that add new tweens
    // don't trip the loop.
    const snapshot = this.active.slice();
    for (const t of snapshot) {
      if (!t.isAlive()) continue;
      t.step(deltaMs);
    }
    // Compact: drop completed/killed tweens.
    this.active = this.active.filter((t) => t.isAlive());
  }

  /** Number of active tweens. Useful for diagnostics + tests. */
  size(): number {
    return this.active.length;
  }

  /** Kill all tweens and clear. Used on scene shutdown. */
  reset(): void {
    for (const t of this.active) t.kill();
    this.active = [];
  }
}

export const TweenHost = new TweenHostImpl();

/**
 * Translate a Phaser-style ease name (e.g. `'Cubic.Out'`) to our
 * Easing function. Falls back to linear for unrecognized names —
 * callers should migrate to direct Easing references over time.
 */
export function mapEase(phaserEase: string | undefined): EasingFn {
  if (!phaserEase) return Easing.linear;
  switch (phaserEase) {
  case 'Linear':
    return Easing.linear;
  case 'Sine.easeInOut':
  case 'Sine.InOut':
  case 'Sine':
    return Easing.sineInOut;
  case 'Sine.easeOut':
  case 'Sine.Out':
    return Easing.sineOut;
  case 'Cubic.easeOut':
  case 'Cubic.Out':
  case 'Cubic':
    return Easing.cubicOut;
  case 'Power2':
  case 'Power2.easeIn':
  case 'Quad.easeIn':
  case 'Quad':
    return Easing.power2;
  case 'Power2.easeOut':
  case 'Quad.easeOut':
    return Easing.power2Out;
  case 'Power2.easeInOut':
  case 'Quad.easeInOut':
    return Easing.power2InOut;
  case 'Back.easeOut':
  case 'Back.Out':
  case 'Back':
    // Easing.backIn is the in-curve; Phaser Back.Out is the reflected
    // form. cubicOut is a close visual match for "decelerating overshoot
    // alternative" — but for true Back.Out we'd need a dedicated curve.
    // Keep cubicOut as the closest existing curve until we add backOut.
    return Easing.cubicOut;
  case 'Back.easeIn':
  case 'Back.In':
    return Easing.backIn;
  default:
    return Easing.linear;
  }
}
